// src/constants/rotaOptions.ts
export interface RotaOption {
  id: string;
  label: string;
  color: string;
  startTime: string; 
  endTime: string;  
  shortLabel: string;
  isPatternFriendly: boolean;
  description: string;
}

export const ROTA_OPTIONS: RotaOption[] = [
  // 🏢 CORE WORK OPTIONS (Most common)
  { 
    id: 'working', 
    label: 'WORKING', 
    color: 'bg-pink-500 text-white',
    startTime: '10:00',
    endTime: '18:00',
    shortLabel: 'WORK',
    isPatternFriendly: true,
    description: 'Regular working day'
  },
  { 
    id: 'am', 
    label: 'AM (10-2pm)', 
    color: 'bg-green-600 text-white', 
    startTime: '10:00',
    endTime: '14:00',
    shortLabel: 'AM',
    isPatternFriendly: true,
    description: 'Morning Work (10am - 2pm)'
  },
  { 
    id: 'pm', 
    label: 'PM (2-6pm)', 
    color: 'bg-lime-500 text-white',
    startTime: '14:00',
    endTime: '18:00',
    shortLabel: 'PM',
    isPatternFriendly: true,
    description: 'Afternoon Work (2pm - 6pm)'
  },
  { 
    id: 'programming', 
    label: 'Programming', 
    color: 'bg-purple-500 text-white',
    startTime: '10:00',
    endTime: '18:00',
    shortLabel: 'PROG',
    isPatternFriendly: false,
    description: 'Programming and planning work'
  },

  // 🎭 EVENT & VENUE WORK
  { 
    id: 'event', 
    label: 'EVENT', 
    color: 'bg-purple-400 text-white',
    startTime: '15:00',
    endTime: '23:00',
    shortLabel: 'EVENT',
    isPatternFriendly: false,
    description: 'Working at an event'
  },
  { 
    id: 'cooler', 
    label: 'COOLER', 
    color: 'bg-blue-400 text-white', 
    startTime: '10:00',
    endTime: '18:00',
    shortLabel: 'COOL',
    isPatternFriendly: false,
    description: 'Working at The Cooler venue'
  },

  // ✈️ TRAVEL & REMOTE WORK
  { 
    id: 'traveling', 
    label: 'Traveling', 
    color: 'bg-yellow-500 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'TRAVEL',
    isPatternFriendly: false,
    description: 'Traveling for work purposes'
  },
  { 
    id: 'abroad', 
    label: 'Abroad', 
    color: 'bg-blue-600 text-white', 
    startTime: '08:00',
    endTime: '20:00',
    shortLabel: 'ABROAD',
    isPatternFriendly: false,
    description: 'Working abroad or traveling for work'
  },

  // 📅 MEETINGS & ADMIN
  {
    id: 'meeting',
    label: 'Meeting',
    color: 'bg-blue-700 text-white',
    startTime: '10:00',
    endTime: '11:00',
    shortLabel: 'MEET',
    isPatternFriendly: false,
    description: 'Scheduled meeting or conference call'
  },
  {
    id: 'staff-meeting',
    label: 'Staff Meeting (All Hands)',
    color: 'bg-violet-700 text-white',
    startTime: '10:00',
    endTime: '11:00',
    shortLabel: 'ALL HANDS',
    isPatternFriendly: false,
    description: 'All-hands staff meeting — GM/SuperAdmin only'
  },
  { 
    id: 'working-unavail', 
    label: 'Working but Unavail...', 
    color: 'bg-red-400 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'W-UN',
    isPatternFriendly: false,
    description: 'Working but unavailable for additional tasks'
  },

  // 🏖️ LEAVE & TIME OFF
  { 
    id: 'annual-leave', 
    label: 'Annual Leave', 
    color: 'bg-green-500 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'LEAVE',
    isPatternFriendly: false,
    description: 'Scheduled annual leave'
  },
  { 
    id: 'time-lieu', 
    label: 'Time In Lieu', 
    color: 'bg-yellow-600 text-white',
    startTime: '10:00',
    endTime: '14:00', 
    shortLabel: 'TIL',
    isPatternFriendly: false,
    description: 'Time off in lieu'
  },
  { 
    id: 'sick-leave', 
    label: 'Sick Leave', 
    color: 'bg-gray-700 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'SICK',
    isPatternFriendly: false,
    description: 'Sick leave'
  },

  // 🏛️ COMPANY & HOLIDAYS
  { 
    id: 'company-leave', 
    label: 'Company Leave', 
    color: 'bg-teal-400 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'COMP',
    isPatternFriendly: false,
    description: 'Company-wide leave or closure - Admin use only'
  },
  { 
    id: 'bank-holiday', 
    label: 'Bank Holiday', 
    color: 'bg-orange-400 text-white', 
    startTime: '10:00',
    endTime: '18:00',
    shortLabel: 'BANK',
    isPatternFriendly: false,
    description: 'Public bank holiday - Admin use only'
  },

  // ❌ NOT WORKING
  { 
    id: 'not-working', 
    label: 'Not Working', 
    color: 'bg-gray-500 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'OFF',
    isPatternFriendly: true,
    description: 'Not scheduled to work - this is typically for part-time employees'
  },
  { 
    id: 'unavailable', 
    label: 'Unavailable', 
    color: 'bg-red-600 text-white',
    startTime: '10:00',
    endTime: '18:00', 
    shortLabel: 'UNAV',
    isPatternFriendly: true,
    description: 'Unavailable for work - use this if you\'re unavailable for any reason'
  }
];

// Staff member color themes for multi-staff visibility
export const STAFF_COLORS: string[] = [
  'bg-blue-100 border-blue-300 text-blue-900',
  'bg-green-100 border-green-300 text-green-900', 
  'bg-purple-100 border-purple-300 text-purple-900',
  'bg-pink-100 border-pink-300 text-pink-900',
  'bg-orange-100 border-orange-300 text-orange-900',
  'bg-indigo-100 border-indigo-300 text-indigo-900',
  'bg-red-100 border-red-300 text-red-900',
  'bg-yellow-100 border-yellow-300 text-yellow-900',
  'bg-teal-100 border-teal-300 text-teal-900',
  'bg-cyan-100 border-cyan-300 text-cyan-900'
];

// Helper functions
export const getRotaOptionById = (id: string): RotaOption | undefined => {
  return ROTA_OPTIONS.find(option => option.id === id);
};

export const getPatternFriendlyOptions = (): RotaOption[] => {
  return ROTA_OPTIONS.filter(option => option.isPatternFriendly);
};

export const getStaffColorByIndex = (index: number): string => {
  return STAFF_COLORS[index % STAFF_COLORS.length];
};

export const isWeekday = (date: Date): boolean => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday to Friday
};

export const getDayName = (dayIndex: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayIndex];
};

export const shouldShowPatternDialog = (optionId: string, date: Date): boolean => {
  const option = getRotaOptionById(optionId);
  return option ? option.isPatternFriendly && isWeekday(date) : false;
};

// Color mapping for MUI components (since we can't use Tailwind classes directly in MUI)
export const getMuiColorForOption = (optionId: string): string => {
  const colorMap: { [key: string]: string } = {
    'working': '#ec4899', // pink.500
    'programming': '#8b5cf6', // purple.500  
    'event': '#a855f7', // purple.400
    'am': '#059669', // green.600
    'abroad': '#2563eb', // blue.600
    'bank-holiday': '#fb923c', // orange.400
    'pm': '#84cc16', // lime.500
    'traveling': '#eab308', // yellow.500
    'meeting': '#1d4ed8', // blue.700
    'staff-meeting': '#6d28d9', // violet.700
    'company-leave': '#14b8a6', // teal.400
    'cooler': '#60a5fa', // blue.400
    'working-unavail': '#f87171', // red.400
    'not-working': '#6b7280', // gray.500
    'time-lieu': '#ca8a04', // yellow.600
    'unavailable': '#dc2626', // red.600
    'annual-leave': '#10b981', // green.500
    'sick-leave': '#374151' // gray.700
  };
  
  return colorMap[optionId] || '#6b7280';
};